import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Application } from '../types';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Key, 
  Copy, 
  Check, 
  X, 
  Globe,
  Eye,
  EyeOff,
  RefreshCw,
  Loader2
} from 'lucide-react';

export function ApplicationsPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.getApplications(1, 100),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const handleEdit = (app: Application) => {
    setEditingApp(app);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingApp(null);
    setShowModal(true);
  };

  const handleDelete = async (app: Application) => {
    if (confirm(`Are you sure you want to delete "${app.name}"? This will remove all subscriptions and notifications.`)) {
      deleteMutation.mutate(app.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-slate-400">Manage your applications and API keys</p>
        <button onClick={handleCreate} className="btn btn-primary flex items-center gap-2">
          <Plus className="h-5 w-5" />
          New Application
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid gap-6">
          {data?.data?.map((app) => (
            <ApplicationCard
              key={app.id}
              application={app}
              onEdit={() => handleEdit(app)}
              onDelete={() => handleDelete(app)}
            />
          ))}
          {data?.data?.length === 0 && (
            <div className="card text-center py-12">
              <p className="text-slate-400">No applications yet. Create your first one!</p>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ApplicationModal
          application={editingApp}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

interface ApplicationCardProps {
  application: Application;
  onEdit: () => void;
  onDelete: () => void;
}

function ApplicationCard({ application, onEdit, onDelete }: ApplicationCardProps) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const regenerateMutation = useMutation({
    mutationFn: () => api.regenerateApiKey(application.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = () => {
    if (confirm('Are you sure you want to regenerate the API key? The old key will stop working immediately.')) {
      regenerateMutation.mutate();
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{application.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs ${
              application.isActive 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {application.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Created: {new Date(application.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="btn btn-secondary p-2" title="Edit">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="btn btn-danger p-2" title="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* API Key */}
      <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-300">API Key</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowKey(!showKey)}
              className="text-slate-400 hover:text-white"
              title={showKey ? 'Hide' : 'Show'}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={() => copyToClipboard(application.apiKey)}
              className="text-slate-400 hover:text-white"
              title="Copy"
            >
              {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
            </button>
            <button
              onClick={handleRegenerate}
              disabled={regenerateMutation.isPending}
              className="text-slate-400 hover:text-white disabled:opacity-50"
              title="Regenerate"
            >
              <RefreshCw className={`h-4 w-4 ${regenerateMutation.isPending ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <code className="text-sm text-blue-400 break-all">
          {showKey ? application.apiKey : '••••••••••••••••••••••••••••••••'}
        </code>
      </div>

      {/* Allowed Origins */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Allowed Origins</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {application.allowedOrigins?.length > 0 ? (
            application.allowedOrigins.map((origin, i) => (
              <span key={i} className="px-2 py-1 bg-slate-700 rounded text-sm text-slate-300">
                {origin}
              </span>
            ))
          ) : (
            <span className="text-sm text-slate-500">All origins allowed</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-700">
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{application._count?.subscriptions || 0}</p>
          <p className="text-sm text-slate-400">Subscriptions</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{application._count?.notifications || 0}</p>
          <p className="text-sm text-slate-400">Notifications</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{application.rateLimit}</p>
          <p className="text-sm text-slate-400">Rate Limit/min</p>
        </div>
      </div>
    </div>
  );
}

interface ApplicationModalProps {
  application: Application | null;
  onClose: () => void;
}

function ApplicationModal({ application, onClose }: ApplicationModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!application;

  const [formData, setFormData] = useState({
    name: application?.name || '',
    allowedOrigins: application?.allowedOrigins?.join('\n') || '',
    webhookUrl: application?.webhookUrl || '',
    rateLimit: application?.rateLimit || 100,
    isActive: application?.isActive ?? true,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Application>) => api.createApplication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Application>) => 
      api.updateApplication(application!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      allowedOrigins: formData.allowedOrigins
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean),
      webhookUrl: formData.webhookUrl || undefined,
      rateLimit: Number(formData.rateLimit),
      isActive: formData.isActive,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">
            {isEditing ? 'Edit Application' : 'New Application'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {error instanceof Error ? error.message : 'An error occurred'}
            </div>
          )}

          <div>
            <label className="label">Application Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="My App"
              required
            />
          </div>

          <div>
            <label className="label">Allowed Origins (one per line)</label>
            <textarea
              value={formData.allowedOrigins}
              onChange={(e) => setFormData({ ...formData, allowedOrigins: e.target.value })}
              className="input min-h-[100px]"
              placeholder="https://myapp.com&#10;http://localhost:3000"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty to allow all origins (not recommended for production)
            </p>
          </div>

          <div>
            <label className="label">Webhook URL (optional)</label>
            <input
              type="url"
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              className="input"
              placeholder="https://myapp.com/webhooks/push"
            />
          </div>

          <div>
            <label className="label">Rate Limit (requests/minute)</label>
            <input
              type="number"
              value={formData.rateLimit}
              onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) })}
              className="input"
              min="1"
              max="10000"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-slate-600 bg-slate-700 text-blue-500"
            />
            <label htmlFor="isActive" className="text-sm text-slate-300">
              Application is active
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
